import { cn } from '@/lib/utils';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';

const TRANSITION_DURATION_MS = 300;

type Direction = 'forward' | 'back';

type Slot = {
    key: string;
    node: ReactNode;

    direction: Direction;
    animate: boolean;
};

function depthForPath(pathname: string): number {
    if (pathname.startsWith('/scheduled-jobs/manage/')) return 2;
    if (
        pathname === '/scheduled-jobs/bank' ||
        pathname === '/scheduled-jobs/new' ||
        pathname.startsWith('/scheduled-jobs/article/')
    ) {
        return 1;
    }
    return 0;
}

// The feed (index or a job-filtered stream) and an article opened from it.
function isFeedPath(pathname: string): boolean {
    return (
        pathname === '/scheduled-jobs' ||
        pathname.startsWith('/scheduled-jobs/stream/')
    );
}

function isArticlePath(pathname: string): boolean {
    return pathname.startsWith('/scheduled-jobs/article/');
}

// Opening/closing an article from the feed is treated as a lateral hop, not
// a step in the push/pop stack — it swaps instantly instead of sliding, so
// clicking through brief cards doesn't play the page-transition animation.
function isFeedArticleHop(a: string, b: string): boolean {
    return (
        (isFeedPath(a) && isArticlePath(b)) ||
        (isArticlePath(a) && isFeedPath(b))
    );
}

/**
 * Layout for the scheduled-jobs routes (feed, bank, create job, ...) that
 * animates route changes as a push/pop navigation stack instead of an
 * instant swap. Keeps the outgoing page mounted for the duration of the
 * animation so both pages are visible mid-transition.
 */
export function ScheduledAgentsPageTransition() {
    const outlet = useOutlet();
    const { pathname } = useLocation();

    const [current, setCurrent] = useState<Slot>({
        key: pathname,
        node: outlet,
        direction: 'forward',
        animate: true
    });
    const [outgoing, setOutgoing] = useState<Slot | null>(null);
    const clearOutgoingTimer = useRef<ReturnType<typeof setTimeout>>();

    // Entering the section from outside (e.g. from the chat page) mounts
    // this component fresh - that first paint shouldn't play the push-in
    // animation, since there's no prior scheduled-jobs page to push in
    // from. Only navigations *within* the section (this component staying
    // mounted while its Outlet child changes) should animate. Only ever
    // flipped from inside the real-transition branch below - NOT from a
    // separate mount effect, since the "same route" resync branch also
    // runs unconditionally on mount and would otherwise still be racing
    // that flag true in time to re-animate this same, not-actually-new
    // DOM node (same key) on the resync's own re-render.
    const hasMountedRef = useRef(false);
    const shouldAnimateEntry = hasMountedRef.current && current.animate;

    useEffect(() => {
        if (pathname === current.key) {
            // Same route re-rendering in place (e.g. a param changing) -
            // keep it in sync without triggering a transition. Direction
            // carries over unchanged since this isn't a real transition.
            setCurrent({
                key: pathname,
                node: outlet,
                direction: current.direction,
                animate: current.animate
            });
            return;
        }

        // A real cross-page navigation inside this mounted instance -
        // from here on, entrances should animate.
        hasMountedRef.current = true;

        const direction: Direction =
            depthForPath(pathname) < depthForPath(current.key)
                ? 'back'
                : 'forward';
        const animate = !isFeedArticleHop(current.key, pathname);

        // The feed<->article hop swaps instantly - no outgoing panel to
        // animate out, just replace `current` in place.
        if (!animate) {
            clearTimeout(clearOutgoingTimer.current);
            setOutgoing(null);
            setCurrent({
                key: pathname,
                node: outlet,
                direction,
                animate: false
            });
            return;
        }

        setOutgoing({ ...current, direction, animate: true });
        setCurrent({ key: pathname, node: outlet, direction, animate: true });

        clearTimeout(clearOutgoingTimer.current);
        clearOutgoingTimer.current = setTimeout(
            () => setOutgoing(null),
            TRANSITION_DURATION_MS
        );

        return () => clearTimeout(clearOutgoingTimer.current);
        // Re-run only when the route actually changes - `outlet`/`current`
        // are derived, not independent triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return (
        <div className="relative w-full h-full overflow-hidden">
            {outgoing && (
                <div
                    key={outgoing.key}
                    className={cn(
                        'absolute inset-0 fill-mode-forwards w-full h-full animate-out duration-300 ease-out',
                        outgoing.direction === 'forward'
                            ? 'z-10 slide-out-to-left-1/4 fade-out-40'
                            : 'z-20 slide-out-to-right'
                    )}
                >
                    {outgoing.node}
                </div>
            )}
            <div
                key={current.key}
                className={cn(
                    'absolute inset-0 w-full h-full',
                    shouldAnimateEntry && [
                        'animate-in ease-out duration-300 fill-mode-forwards',
                        current.direction === 'forward'
                            ? 'z-20 slide-in-from-right shadow-[-24px_0_40px_-24px_rgba(0,0,0,0.2)]'
                            : 'z-10 slide-in-from-left-1/4 fade-in-40'
                    ]
                )}
            >
                {current.node}
            </div>
        </div>
    );
}
